import { Injectable } from '@angular/core';
import { Observable, concatMap, from, last, of, takeWhile } from 'rxjs';

/** A check returns false to keep the user signed in (e.g. they chose to stay on a page). */
export type SignOutCheck = () => Observable<boolean>;

/**
 * Screens with work that must be confirmed before a manual sign-out register a check here.
 * Checks run one after another; sign-out continues only if every check returns true.
 */
@Injectable({ providedIn: 'root' })
export class SignOutConfirmationService {
  private readonly checks = new Set<SignOutCheck>();

  /** Returns a function that removes the check again. */
  register(check: SignOutCheck): () => void {
    this.checks.add(check);
    return () => {
      this.checks.delete(check);
    };
  }

  confirm(): Observable<boolean> {
    const checks = [...this.checks];
    if (checks.length === 0) {
      return of(true);
    }
    return from(checks).pipe(
      concatMap((check) => check()),
      takeWhile((ok) => ok, true),
      last(),
    );
  }
}
